import React, { useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import cn from 'classnames';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { RubricWhiteCard } from 'apps/sigma2-ui/src/components/rubrics/RubricWhiteCard';
import { IconResumeRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconResumeRubric28';
import { IconWrittenResponseRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconWrittenResponseRubric28';
import { IconInterviewRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconInterviewRubric28';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { Dropdown } from 'apps/sigma2-ui/src/components/Dropdown';
import { DropdownItemProps } from 'apps/sigma2-ui/src/components/Dropdown';
import { IconEditOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconEditOutline';
import { MoreItemsIconSvg } from 'apps/sigma2-ui/src/components/svg/MoreItemsIcon';
import { IconDuplicateOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconDuplicateOutline';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { IconTrashCanSvg } from 'apps/sigma2-ui/src/components/svg/IconTrashCan';
import { IconPublishSvg } from 'apps/sigma2-ui/src/components/svg/IconPublish';
import { IconUnpublishSvg } from 'apps/sigma2-ui/src/components/svg/IconUnpublish';
import { IconArchiveSvg } from 'apps/sigma2-ui/src/components/svg/IconArchive';
import { IconUnarchiveSvg } from 'apps/sigma2-ui/src/components/svg/IconUnarchive';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TertiaryButton } from 'apps/sigma2-ui/src/components/TertiaryButton';
import { IconAddSvg } from 'apps/sigma2-ui/src/components/svg/IconAdd';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';

export type RubricPendingActionType =
  | 'archive'
  | 'unarchive'
  | 'delete'
  | 'publish'
  | 'unpublish';

export interface RubricPendingAction {
  rubricId: string;
  action: RubricPendingActionType;
}

export interface RubricToastData {
  id: string;
  type: 'success' | 'error';
  name: string;
  action: RubricPendingActionType;
}

export interface RubricListItemProps {
  id: string;
  type: RubricGradingType;
  name: string;
  jobTitles: string[];
  status: RubricStatus;
  canEdit: boolean;
  editHref: string;
  showActionsMenu: boolean;
  canDuplicate: boolean;
  duplicateHref: string;
  canArchive: boolean;
  archivePending: boolean;
  archiveFn: () => void;
  canUnarchive: boolean;
  unarchivePending: boolean;
  unarchiveFn: () => void;
  canDelete: boolean;
  deletePending: boolean;
  deleteFn: () => void;
  canPublish: boolean;
  publishPending: boolean;
  publishFn: () => void;
  canUnpublish: boolean;
  unpublishPending: boolean;
  unpublishFn: () => void;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}

export interface RubricListProps {
  items: RubricListItemProps[];
}

export const RubricList: React.FC<RubricListProps> = ({ items }) => {
  return (
    <div className="w-full overflow-x-auto">
      <div className="space-y-3 min-w-[920px]">
        {items.map((item) => (
          <RubricListItem key={item.id} {...item} />
        ))}
      </div>
    </div>
  );
};

const RubricListItemActionsMenu: React.FC<{
  showActionsMenu: boolean;
  dropdownItems: DropdownItemProps[];
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}> = ({ showActionsMenu, dropdownItems, menuOpen, setMenuOpen }) => {
  if (!showActionsMenu || dropdownItems.length === 0) {
    return null;
  }

  return (
    <Dropdown
      trigger={<MoreItemsIconSvg />}
      items={dropdownItems}
      isOpen={menuOpen}
      setIsOpen={setMenuOpen}
    />
  );
};

export const RubricListItem: React.FC<RubricListItemProps> = (props) => {
  const language = useLanguage();

  const iconMap: Record<RubricListItemProps['type'], React.FC<SvgProps>> = {
    resume: IconResumeRubric28Svg,
    questions: IconWrittenResponseRubric28Svg,
    interview: IconInterviewRubric28Svg,
  };

  const statusColorMap: Record<RubricListItemProps['status'], string> = {
    draft: 'bg-gray-200',
    published: 'bg-green-200',
    archived: 'bg-gray-200',
  };

  const statusTextMap: Record<RubricListItemProps['status'], string> = {
    draft: language.resumeStatusDraftLabel(),
    published: language.resumeStatusLiveLabel(),
    archived: language.resumeStatusArchivedLabel(),
  };

  const anyPending =
    props.archivePending ||
    props.unarchivePending ||
    props.deletePending ||
    props.publishPending ||
    props.unpublishPending;

  const cnOpacityMask = {
    'opacity-30': props.status === 'archived',
  };

  const dropdownItems = useMemo<DropdownItemProps[]>(() => {
    const items = {
      edit: {
        can: props.canEdit,
        pending: false,
        onClick: null,
        href: props.editHref,
        label: language.resumeListKebabEditLabel(),
        icon: IconEditOutlineSvg,
        isDestructive: false,
      },
      duplicate: {
        can: props.canDuplicate,
        pending: false,
        onClick: null,
        href: props.duplicateHref,
        label: language.resumeListKebabDuplicateLabel(),
        icon: IconDuplicateOutlineSvg,
        isDestructive: false,
      },
      publish: {
        can: props.canPublish,
        pending: props.publishPending,
        onClick: props.publishFn,
        href: null,
        label: language.resumeListKebabPublishLabel(),
        icon: IconPublishSvg,
        isDestructive: false,
      },
      unpublish: {
        can: props.canUnpublish,
        pending: props.unpublishPending,
        onClick: props.unpublishFn,
        href: null,
        label: language.resumeListKebabUnpublishLabel(),
        icon: IconUnpublishSvg,
        isDestructive: false,
      },
      archive: {
        can: props.canArchive,
        pending: props.archivePending,
        onClick: props.archiveFn,
        href: null,
        label: language.resumeListKebabArchiveLabel(),
        icon: IconArchiveSvg,
        isDestructive: false,
      },
      unarchive: {
        can: props.canUnarchive,
        pending: props.unarchivePending,
        onClick: props.unarchiveFn,
        href: null,
        label: language.resumeListKebabUnarchiveLabel(),
        icon: IconUnarchiveSvg,
        isDestructive: false,
      },
      delete: {
        can: props.canDelete,
        pending: props.deletePending,
        onClick: props.deleteFn,
        href: null,
        label: language.resumeListKebabDeleteLabel(),
        icon: IconTrashCanSvg,
        isDestructive: true,
      },
    };

    const result: DropdownItemProps[] = [];

    for (const [key, item] of Object.entries(items)) {
      const textColorClass = item.isDestructive
        ? 'text-red-700'
        : 'text-gray-800';

      if (item.can) {
        result.push({
          key,
          content: (
            <div className={cn('flex items-center gap-2', textColorClass)}>
              {item.pending ? (
                <Spinner size={18} textColor={textColorClass} />
              ) : (
                <item.icon svg={{ className: 'h-4 w-4' }} />
              )}
              <S2Typography
                variant="body-6"
                textColorVariant={item.isDestructive ? 'red-700' : 'gray-800'}
                translatedContent={item.label}
              />
            </div>
          ),
          behavior:
            anyPending || !item.onClick
              ? item.href
                ? { type: 'href' as const, href: item.href }
                : undefined
              : { type: 'onClick' as const, onClick: item.onClick },
        });
      }
    }

    return result;
  }, [
    anyPending,
    language,
    props.archiveFn,
    props.archivePending,
    props.canArchive,
    props.canDelete,
    props.canDuplicate,
    props.canEdit,
    props.canPublish,
    props.canUnarchive,
    props.canUnpublish,
    props.deleteFn,
    props.deletePending,
    props.duplicateHref,
    props.editHref,
    props.publishFn,
    props.publishPending,
    props.unarchiveFn,
    props.unarchivePending,
    props.unpublishFn,
    props.unpublishPending,
  ]);

  const nameTextPart = (
    <>
      <S2Typography
        variant="headline-6-alt"
        textColorVariant="gray-600"
        translatedContent={language.rubricListItemTitle()}
      />
      <div className="mt-1">
        <S2Typography
          variant="body-6-bold"
          textColorVariant="gray-1000"
          translatedContent={[{ text: props.name, tooltip: props.name }]}
          truncate={true}
        />
      </div>
    </>
  );

  return (
    <RubricWhiteCard
      className="w-full transition-colors duration-200 hover:bg-[#FBFAFF]"
      roundedClassName="rounded-2xl"
      paddingClassName={'p-5'}
    >
      <div className="grid w-full grid-cols-[8px_minmax(200px,1fr)_320px_120px_80px_24px] items-center gap-x-6">
        <div
          className={cn(
            'h-12 rounded-full',
            {
              'bg-indigo-200': props.type === 'resume',
              'bg-indigo-900': props.type === 'questions',
              'bg-indigo-700': props.type === 'interview',
            },
            cnOpacityMask,
          )}
        />

        <div className={cn(`min-w-0`, cnOpacityMask)}>
          {props.canEdit ? (
            <TouchTarget
              className={'w-full'}
              behavior={{
                type: 'href',
                href: props.editHref,
              }}
              content={() => nameTextPart}
            />
          ) : (
            nameTextPart
          )}
        </div>

        <div className={cn(`min-w-0`, cnOpacityMask)}>
          <S2Typography
            variant="headline-6-alt"
            textColorVariant="gray-600"
            translatedContent={language.rubricJobTitlesLabel()}
          />
          <div className="mt-1">
            <S2Typography
              as={'span'}
              variant="body-6"
              textColorVariant="gray-1000"
              translatedContent={language.rubricListItemJobTitles({
                jobTitles: props.jobTitles,
              })}
            />
          </div>
        </div>

        <div className={cn(`justify-self-start`, cnOpacityMask)}>
          <div className="inline-flex items-center gap-2">
            {iconMap[props.type] &&
              iconMap[props.type]({ svg: { className: 'w-[28px] h-[28px]' } })}
            <S2Typography
              variant={'body-6-bold'}
              translatedContent={
                props.type === 'resume'
                  ? language.resumeRubricShortTitle()
                  : props.type === 'questions'
                    ? language.writtenResponseRubricShortTitle()
                    : language.interviewRubricShortTitle()
              }
              textColorVariant={'indigo-900'}
            />
          </div>
        </div>

        <div className={cn('flex items-center gap-2', cnOpacityMask)}>
          <span
            className={cn(
              `h-2.5 w-2.5 rounded-full`,
              statusColorMap[props.status],
            )}
          />
          <S2Typography
            variant="body-6-bold"
            textColorVariant={
              props.status === 'published' ? 'green-600' : 'gray-600'
            }
            translatedContent={statusTextMap[props.status]}
          />
        </div>

        <RubricListItemActionsMenu
          showActionsMenu={props.showActionsMenu}
          dropdownItems={dropdownItems}
          menuOpen={props.menuOpen}
          setMenuOpen={props.setMenuOpen}
        />
      </div>
    </RubricWhiteCard>
  );
};

export interface RubricActionConfirmationModalProps {
  name: string;
  action: RubricPendingActionType;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RubricActionConfirmationModal: React.FC<
  RubricActionConfirmationModalProps
> = (props) => {
  const { name, action } = props;

  const language = useLanguage();

  const copyMap: Record<
    RubricPendingActionType,
    { title: TranslatedContent; description: string; confirmLabel: string }
  > = {
    delete: {
      title: language.rubricConfirmationPromptTitleDelete({
        name,
      }),
      description: language.rubricConfirmationPromptDescriptionDelete(),
      confirmLabel: language.rubricConfirmationPromptConfirmLabelDelete(),
    },
    archive: {
      title: language.rubricConfirmationPromptTitleArchive({
        name,
      }),
      description: language.rubricConfirmationPromptDescriptionArchive(),
      confirmLabel: language.rubricConfirmationPromptConfirmLabelArchive(),
    },
    publish: {
      title: language.rubricConfirmationPromptTitlePublish({
        name,
      }),
      description: language.rubricConfirmationPromptDescriptionPublish(),
      confirmLabel: language.rubricConfirmationPromptConfirmLabelPublish(),
    },
    unarchive: {
      title: language.rubricConfirmationPromptTitleUnarchive({
        name,
      }),
      description: language.rubricConfirmationPromptDescriptionUnarchive(),
      confirmLabel: language.rubricConfirmationPromptConfirmLabelUnarchive(),
    },
    unpublish: {
      title: language.rubricConfirmationPromptTitleUnpublish({
        name,
      }),
      description: language.rubricConfirmationPromptDescriptionUnpublish(),
      confirmLabel: language.rubricConfirmationPromptConfirmLabelUnpublish(),
    },
  };

  const copy = copyMap[action];

  return (
    <Dialog
      open={true}
      onClose={() => (!props.pending ? props.onCancel() : {})}
    >
      <div className="fixed inset-0 z-[100] bg-gray-1000/20" />
      <div className="fixed inset-0 z-[101] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-[452px] rounded-[28px] bg-white p-7 shadow-[0_20px_60px_rgba(16,24,40,0.16)]">
            <Dialog.Title as="div">
              <S2Typography
                variant="headline-3"
                textColorVariant="gray-1000"
                translatedContent={copy.title}
              />
            </Dialog.Title>

            <div className="mt-5">
              <S2Typography
                variant="body-6"
                textColorVariant="gray-600"
                translatedContent={copy.description}
              />
            </div>

            <div className="mt-8 flex flex-col gap-4">
              <button
                type="button"
                onClick={props.onConfirm}
                disabled={props.pending}
                className={cn(
                  'flex h-12 w-full items-center justify-center rounded-[8px] bg-indigo-950 px-4 gap-2',
                  props.pending ? 'cursor-not-allowed opacity-70' : '',
                )}
              >
                {props.pending ? (
                  <>
                    <Spinner size={18} textColor="text-white" />
                    <S2Typography
                      variant="body-6"
                      textColorVariant="white"
                      translatedContent={copy.confirmLabel}
                    />
                  </>
                ) : (
                  <S2Typography
                    variant="body-6"
                    textColorVariant="white"
                    translatedContent={copy.confirmLabel}
                  />
                )}
              </button>

              <button
                type="button"
                onClick={props.onCancel}
                disabled={props.pending}
                className={cn(
                  'flex h-10 w-full items-center justify-center px-4',
                  props.pending ? 'cursor-not-allowed opacity-70' : '',
                )}
              >
                <S2Typography
                  variant="body-6"
                  textColorVariant="indigo-900"
                  translatedContent={language.cancel()}
                />
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export interface RubricPublishSuccessModalProps {
  name: string;
  onClose: () => void;
}

export const RubricPublishSuccessModal: React.FC<
  RubricPublishSuccessModalProps
> = (props) => {
  const language = useLanguage();

  return (
    <Dialog open={true} onClose={props.onClose}>
      <div className="fixed inset-0 z-[100] bg-gray-1000/20" />
      <div className="fixed inset-0 z-[101] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-[308px] overflow-hidden rounded-[28px] bg-white shadow-[0_20px_60px_rgba(16,24,40,0.16)]">
            <div className="px-6 pb-6 pt-5 text-center">
              <Spacing size={'sm'} direction={'vertical'}>
                <img
                  className="w-[266px]"
                  src="/images/rubrics/rubric-confirm-publish.png"
                  alt=""
                />

                <S2Typography
                  variant="headline-2"
                  textColorVariant="gray-1000"
                  translatedContent={language.rubricPublishedModalTitle()}
                />

                <S2Typography
                  variant="body-6"
                  textColorVariant="gray-700"
                  translatedContent={language.rubricPublishedModalDescription({
                    name: props.name,
                  })}
                />
              </Spacing>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export interface RubricToastStackProps {
  toasts: RubricToastData[];
  onDismiss: (toastId: string) => void;
}

export const RubricToastStack: React.FC<RubricToastStackProps> = (props) => {
  const [dismissingToasts, setDismissingToasts] = React.useState<Set<string>>(
    new Set(),
  );
  const [toastCache, setToastCache] = React.useState<
    Map<string, RubricToastData>
  >(new Map());
  const previousToastIdsRef = React.useRef<Set<string>>(new Set());
  const language = useLanguage();

  // Detect when toasts are added or removed and trigger animations
  React.useEffect(() => {
    const currentToastIds = new Set(props.toasts.map((t) => t.id));
    const previousToastIds = previousToastIdsRef.current;

    // Update cache with current toasts
    setToastCache((prevCache) => {
      const newCache = new Map(prevCache);
      props.toasts.forEach((toast) => {
        newCache.set(toast.id, toast);
      });
      return newCache;
    });

    // Find toasts that were removed (either manually or by timer)
    const removedToastIds = Array.from(previousToastIds).filter(
      (id) => !currentToastIds.has(id),
    );

    if (removedToastIds.length > 0) {
      // Mark them as dismissing to trigger animation
      setDismissingToasts((prev) => {
        const next = new Set(prev);
        removedToastIds.forEach((id) => next.add(id));
        return next;
      });

      // Remove from cache after animation completes
      setTimeout(() => {
        setToastCache((prevCache) => {
          const nextCache = new Map(prevCache);
          removedToastIds.forEach((id) => nextCache.delete(id));
          return nextCache;
        });
        setDismissingToasts((prev) => {
          const next = new Set(prev);
          removedToastIds.forEach((id) => next.delete(id));
          return next;
        });
      }, 300);
    }

    previousToastIdsRef.current = currentToastIds;
  }, [props.toasts]);

  // Render toasts from cache (includes those animating out)
  const toastsToRender = Array.from(toastCache.values());

  return (
    <div className="pointer-events-none fixed right-6 top-6 z-[120] flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-3">
      {toastsToRender.map((toast) => {
        const currentState: `${RubricPendingActionType}-${RubricToastData['type']}` = `${toast.action}-${toast.type}`;

        const toastMessageMap: Record<
          `${RubricPendingActionType}-${RubricToastData['type']}`,
          TranslatedContent
        > = {
          'archive-error': language.rubricToastArchiveError({
            name: toast.name,
          }),
          'archive-success': language.rubricToastArchiveSuccess({
            name: toast.name,
          }),
          'delete-error': language.rubricToastDeleteError({ name: toast.name }),
          'delete-success': language.rubricToastDeleteSuccess({
            name: toast.name,
          }),
          'publish-error': language.rubricToastPublishError({
            name: toast.name,
          }),
          'publish-success': language.rubricToastPublishSuccess({
            name: toast.name,
          }),
          'unarchive-error': language.rubricToastUnarchiveError({
            name: toast.name,
          }),
          'unarchive-success': language.rubricToastUnarchiveSuccess({
            name: toast.name,
          }),
          'unpublish-error': language.rubricToastUnpublishError({
            name: toast.name,
          }),
          'unpublish-success': language.rubricToastUnpublishSuccess({
            name: toast.name,
          }),
        };

        const toastMessage = toastMessageMap[currentState];

        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex items-center gap-3 rounded-[16px] border border-gray-200 bg-white px-4 py-4 shadow-[0_10px_30px_rgba(16,24,40,0.12)] transition-all duration-300',
              dismissingToasts.has(toast.id)
                ? 'translate-x-[120%] opacity-0'
                : 'translate-x-0 opacity-100',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px]',
                toast.type === 'success' ? 'bg-green-100' : 'bg-red-100',
              )}
            >
              {toast.type === 'success' ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-green-700"
                >
                  <path
                    d="M4 9.2L7.2 12.2L14 5.5"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-red-700"
                >
                  <path
                    d="M5 5L13 13M13 5L5 13"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </div>

            <div className="min-w-0 flex-1 text-[15px] leading-[1.4] text-gray-700">
              <S2Typography
                variant="body-6"
                textColorVariant="gray-700"
                translatedContent={toastMessage}
              />
            </div>

            <button
              type="button"
              onClick={() => props.onDismiss(toast.id)}
              className="text-gray-500 transition hover:text-gray-800"
              aria-label="Dismiss toast"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4L12 12M12 4L4 12"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export interface RubricListEmptyStateProps {
  interviewRubricHref: string;
  resumeRubricHref: string;
  writtenResponseRubricHref: string;
  canCreateRubrics: boolean;
}

export const RubricListEmptyState: React.FC<RubricListEmptyStateProps> = (
  props,
) => {
  const language = useLanguage();

  const buttons = [
    {
      label: language.resumeRubricShortTitle(),
      href: props.resumeRubricHref,
    },
    {
      label: language.writtenResponseRubricMediumTitle(),
      href: props.writtenResponseRubricHref,
    },
    {
      label: language.interviewRubricShortTitle(),
      href: props.interviewRubricHref,
    },
  ];
  return (
    <RubricWhiteCard className={'w-full pt-20 pb-24'}>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <img
            src="/images/rubrics/rubric-empty-state.png"
            className="select-none"
            alt=""
          />

          <S2Typography
            variant="headline-4"
            textColorVariant="gray-900"
            translatedContent={language.listRubricsEmptyStateTitle()}
          />
          {props.canCreateRubrics && (
            <S2Typography
              variant="body-6"
              textColorVariant="gray-700"
              translatedContent={language.listRubricsEmptyStateDescription()}
            />
          )}

          {props.canCreateRubrics && (
            <div className="flex flex-wrap justify-center gap-2 pt-8">
              {buttons.map((button, buttonIndex) => (
                <TertiaryButton
                  key={buttonIndex}
                  label={button.label}
                  behavior={{ type: 'href', href: button.href }}
                  className={
                    'rounded border-[2px] py-2 px-4 border-indigo-200 whitespace-nowrap'
                  }
                  icon={<IconAddSvg />}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </RubricWhiteCard>
  );
};

export const RubricListLoadingState = () => {
  return (
    <RubricWhiteCard className={'w-full py-[220px]'}>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <Spinner size={48} textColor={'text-indigo-900'} />
        </div>
      </div>
    </RubricWhiteCard>
  );
};

export const RubricListErrorState = () => {
  const language = useLanguage();

  return (
    <RubricWhiteCard className={'w-full py-[220px]'}>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <img
            src="/images/rubrics/rubric-error-state.png"
            className="select-none"
            alt=""
          />

          <S2Typography
            variant="headline-4"
            textColorVariant="gray-900"
            translatedContent={language.listRubricsErrorStateTitle()}
          />
          <S2Typography
            variant="body-6"
            textColorVariant="gray-700"
            translatedContent={language.listRubricsErrorStateDescription()}
          />
        </div>
      </div>
    </RubricWhiteCard>
  );
};

export interface RubricListTabsProps {
  currentTab: 'all' | RubricStatus;
  showLiveTab: boolean;
  showDraftTab: boolean;
  showArchivedTab: boolean;
  onAllTabClick: () => void;
  onLiveTabClick: () => void;
  onDraftTabClick: () => void;
  onArchivedTabClick: () => void;
}
export const RubricListTabs: React.FC<RubricListTabsProps> = (props) => {
  const tabs = [
    { tabValue: 'all' },
    ...(props.showLiveTab ? [{ tabValue: 'published' }] : []),
    ...(props.showDraftTab ? [{ tabValue: 'draft' }] : []),
    ...(props.showArchivedTab ? [{ tabValue: 'archived' }] : []),
  ];
  const language = useLanguage();

  return (
    <TabsRadioGroup
      currValue={props.currentTab}
      values={tabs}
      onChange={(value: string) => {
        if (value === 'all') {
          props.onAllTabClick();
        } else if (value === 'published') {
          props.onLiveTabClick();
        } else if (value === 'draft') {
          props.onDraftTabClick();
        } else if (value === 'archived') {
          props.onArchivedTabClick();
        }
      }}
      minWidth="auto"
      gap="md"
      underlineBgClass="bg-secondary"
      renderTab={({ value }) => {
        const titleMap: Record<string, string> = {
          all: language.listRubricsAllTabTitle(),
          published: language.listRubricsLiveTabTitle(),
          draft: language.listRubricsDraftTabTitle(),
          archived: language.listRubricsArchivedTabTitle(),
        };
        const title = titleMap[value.tabValue] || '';
        return (
          <S2Typography
            variant="headline-6-alt"
            translatedContent={title}
            dangerouslyOverrideClassNameFn={
              value.tabValue === props.currentTab
                ? replaceTextColor('text-secondary')
                : undefined
            }
          />
        );
      }}
    />
  );
};
